import { useEffect } from "react";
import { useLocation } from "react-router";

import { ROUTE_NAMES } from "../constants";

const BASE_TITLE = "Centro Portal";

// Maps a top-level section to its list label and the label used when a
// specific record id is in the path (e.g. /rfps -> "RFP list",
// /rfps/740 -> "RFP 740").
const SECTION_TITLES: Record<string, { list: string; record?: string }> = {
  [ROUTE_NAMES.RFPS]: { list: "RFP list", record: "RFP" },
  [ROUTE_NAMES.CLIENTS]: { list: "Client list", record: "Client" },
  [ROUTE_NAMES.BROKERS]: { list: "Broker list", record: "Broker" },
  [ROUTE_NAMES.BBE]: { list: "Broker Bonus Estimator", record: "Broker" },
  [ROUTE_NAMES.CONSULTING]: { list: "Consulting" },
  [ROUTE_NAMES.ANALYSTS]: { list: "Analysts" },
  [ROUTE_NAMES.PROFILE]: { list: "Profile" },
  [ROUTE_NAMES.USERS]: { list: "Users" },
  [ROUTE_NAMES.RFP]: { list: "New RFP" },
  [ROUTE_NAMES.CLIENT_SELECTION]: { list: "Client selection" },
  [ROUTE_NAMES.CARRIERS_ORDER]: { list: "Carrier order" },
};

// Workbench tab segment (third path part of /rfps/:id/<tab>) -> label.
const RFP_TAB_TITLES: Record<string, string> = {
  [ROUTE_NAMES.RFP_WORKBENCH_LAUNCHPAD]: "Rumi Launchpad",
  [ROUTE_NAMES.RFP_WORKBENCH_DETAILS]: "Details",
  [ROUTE_NAMES.RFP_CARRIER_TRACKER]: "Carrier tracker",
  [ROUTE_NAMES.RFP_WORKBENCH_BUILD_2]: "Build",
  [ROUTE_NAMES.RFP_WORKBENCH_CENSUS]: "Census",
  [ROUTE_NAMES.RFP_WORKBENCH_TRACK]: "Track",
  [ROUTE_NAMES.RFP_WORKBENCH_PRESENT]: "Present",
  [ROUTE_NAMES.RFP_WORKBENCH_DELIVERABLES]: "Deliverables",
  [ROUTE_NAMES.RFP_WORKBENCH_FINAL]: "Final",
};

// Present sub-tab segment (/rfps/:id/present/<subTab>) -> label.
const RFP_PRESENT_TITLES: Record<string, string> = {
  [ROUTE_NAMES.RFP_WORKBENCH_PRESENT_RESPONSES]: "Responses",
  [ROUTE_NAMES.RFP_WORKBENCH_PRESENT_COMPS]: "Comps",
  [ROUTE_NAMES.RFP_WORKBENCH_PRESENT_FINANCIAL]: "Financial",
  [ROUTE_NAMES.RFP_WORKBENCH_PRESENT_QUOTES]: "Quotes",
};

// Decodes a path segment, falling back to the raw value if it carries
// malformed percent-encoding (e.g. a hand-edited URL) so title derivation
// never throws.
function safeDecode(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

// Builds the part after "RFP 740" from the path segments following the id,
// e.g. ["build-2", "forms", "Dental PPO"] -> "Build - Dental PPO".
function rfpDetailSuffix(rest: string[]): string | null {
  const [tab, ...deeper] = rest;
  if (!tab) {
    return "Details";
  }

  const tabTitle = RFP_TAB_TITLES[tab];
  if (!tabTitle) {
    return null;
  }

  // The build-2 forms route carries the plan name: /build-2/forms/:planName.
  // Only treat the segment as a plan name on the actual forms sub-route.
  if (
    tab === ROUTE_NAMES.RFP_WORKBENCH_BUILD_2 &&
    deeper[0] === ROUTE_NAMES.RFP_WORKBENCH_BUILD_2_FORMS &&
    deeper[1]
  ) {
    return `${tabTitle} - ${safeDecode(deeper[1])}`;
  }

  // Present splits into sub-tabs: /present/:subTab.
  if (tab === ROUTE_NAMES.RFP_WORKBENCH_PRESENT) {
    const subTitle = RFP_PRESENT_TITLES[deeper[0]];
    return subTitle ? `${tabTitle} - ${subTitle}` : tabTitle;
  }

  return tabTitle;
}

export function pageTitleForPath(pathname: string): string | null {
  const segments = pathname.split("/").filter(Boolean);
  const [section, recordId] = segments;
  if (!section) {
    return null;
  }

  const config = SECTION_TITLES[section];
  if (!config) {
    return null;
  }

  if (!recordId || !config.record) {
    return config.list;
  }

  const recordTitle = `${config.record} ${recordId}`;

  if (section === ROUTE_NAMES.RFPS) {
    const suffix = rfpDetailSuffix(segments.slice(2));
    return suffix ? `${recordTitle} - ${suffix}` : recordTitle;
  }

  return recordTitle;
}

export function DocumentTitle(): null {
  const { pathname } = useLocation();

  useEffect(() => {
    const pageTitle = pageTitleForPath(pathname);
    document.title = pageTitle ? `${BASE_TITLE} - ${pageTitle}` : BASE_TITLE;
  }, [pathname]);

  return null;
}
